import { useEffect, useRef, useState, type RefObject } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { IconButton } from "./components";
import { RingField, Resolve, tween } from "./fx";
import { bilingual, useLang, useText } from "./i18n";
import { track } from "./usage";

const TEXT = bilingual({
  en: { soundOn: "Sound on", mute: "Mute" },
  es: { soundOn: "Activar sonido", mute: "Silenciar" },
});
/** The landing's promo, voiced in each language. */
const PROMO = {
  en: { src: "/demo/foco-promo.mp4", poster: "/demo/foco-promo.jpg" },
  es: { src: "/demo/foco-promo-es.mp4", poster: "/demo/foco-promo-es.jpg" },
};

/**
 * The promo on a phone: it resolves out of static, then the guide rings drawn on `rings` (a canvas behind it) send
 * one pulse out. It autoplays muted and loops (browsers require the mute); turning the sound on restarts it so the
 * voiceover plays whole, once, ending on its last frame, and muting it again goes back to the silent loop.
 */
export function PromoPhone({
  rings,
}: {
  rings: RefObject<HTMLCanvasElement | null>;
}) {
  const t = useText(TEXT),
    promo = PROMO[useLang()];
  const stage = useRef<HTMLDivElement>(null),
    fx = useRef<HTMLCanvasElement>(null),
    video = useRef<HTMLVideoElement>(null);
  const [resolving, setResolving] = useState(true),
    [sound, setSound] = useState(false);
  const toggleSound = () => {
    const v = video.current!;
    v.muted = sound;
    if (!sound) {
      track("film", "sound");
      v.currentTime = 0;
      void v.play();
    } else if (v.ended) void v.play();
    setSound(!sound);
  };
  useEffect(() => {
    const field = new RingField(rings.current!, stage.current!, {
      alpha: 0.085,
      step: 1.5,
      first: 1.12,
    });
    field.fit();
    field.incoming();
    // the copy around the phone settles once the fonts load, moving the phone without resizing it
    void document.fonts?.ready.then(() => field.fit());
    let cancelled = false;
    const r = new Resolve(fx.current!);
    r.src = video.current;
    r.set(0.05);
    const start = async () => {
      const v = video.current!;
      if (v.readyState < 2)
        await new Promise((res) =>
          v.addEventListener("loadeddata", res, { once: true }),
        );
      if (cancelled) return;
      await tween(1500, (k) => {
        r.set(0.05 + 0.6 * k);
        r.draw(0, true);
      });
      if (cancelled) return;
      await r.lock(900);
      if (!cancelled) {
        setResolving(false);
        field.emit();
      }
    };
    void start();
    return () => {
      cancelled = true;
      field.dispose();
    };
  }, [rings]);
  return (
    <div className="welcome-device">
      <div
        className={`phone ${resolving ? "resolving" : "landed"}`}
        ref={stage}
        aria-hidden="true"
      >
        <div className="screen">
          <video
            ref={video}
            src={promo.src}
            poster={promo.poster}
            muted
            loop={!sound}
            autoPlay
            playsInline
            preload="auto"
            onTimeUpdate={(e) => {
              // how far the promo got with its sound on
              const v = e.currentTarget,
                q = Math.floor((v.currentTime / v.duration) * 4);
              if (sound && q >= 1 && q <= 3) track("film", `${q * 25}%`);
            }}
            onEnded={() => sound && track("film", "100%")}
          />
          {resolving && <canvas ref={fx} className="fx" />}
        </div>
        <span className="edge" />
      </div>
      {!resolving && (
        <IconButton
          label={sound ? t.mute : t.soundOn}
          className="welcome-sound"
          onClick={toggleSound}
        >
          {sound ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </IconButton>
      )}
    </div>
  );
}
